import React, { useState } from 'react';
import { 
  Activity,
  GitBranch,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  Edit,
  Save,
  Plus,
  Search,
  Play,
  Pause,
  Square,
  Check,
  X,
  Code,
  FileText,
  Users,
  Calendar,
  Clock,
  Workflow
} from 'lucide-react';

// Structure de données pour la documentation des processus
const PROCESS_DOCUMENTATION = {
  metadata: {
    title: "Documentation des Processus",
    version: "1.0.0",
    lastUpdate: "2024-03-23",
    author: "Équipe Processus Métier"
  },
  processes: [
    {
      id: "order-management",
      name: "Gestion des Commandes",
      description: "Processus complet de gestion des commandes clients",
      version: "2.1",
      owner: "Service Commercial",
      category: "Ventes",
      states: [
        {
          id: "draft",
          name: "Brouillon",
          description: "Commande en cours de création",
          isInitial: true,
          metadata: {
            retentionDays: 7,
            autoDelete: true
          }
        },
        {
          id: "pending_validation",
          name: "En attente de validation",
          description: "Commande en attente de validation par le service commercial",
          metadata: {
            slaHours: 24,
            notifications: ["commercial_team"]
          }
        },
        {
          id: "validated",
          name: "Validée",
          description: "Commande validée, en attente de paiement"
        },
        {
          id: "paid",
          name: "Payée",
          description: "Paiement reçu, en attente de préparation"
        },
        {
          id: "in_preparation",
          name: "En préparation",
          description: "Commande en cours de préparation"
        },
        {
          id: "shipped",
          name: "Expédiée",
          description: "Commande expédiée au client"
        },
        {
          id: "delivered",
          name: "Livrée",
          description: "Commande livrée au client",
          isFinal: true
        },
        {
          id: "cancelled",
          name: "Annulée",
          description: "Commande annulée",
          isFinal: true,
          metadata: {
            requiresJustification: true
          }
        }
      ],
      transitions: [
        {
          from: "draft",
          to: "pending_validation",
          name: "Soumettre",
          conditions: [
            {
              type: "field_validation",
              description: "Tous les champs obligatoires sont remplis"
            },
            {
              type: "business_rule",
              description: "Montant total > 0"
            }
          ],
          actions: [
            {
              type: "notification",
              description: "Notifier le service commercial"
            },
            {
              type: "audit",
              description: "Enregistrer la date de soumission"
            }
          ]
        },
        {
          from: "pending_validation",
          to: "validated",
          name: "Valider",
          roles: ["commercial_validator"],
          conditions: [
            {
              type: "business_rule",
              description: "Stock disponible"
            },
            {
              type: "user_role",
              description: "Utilisateur avec rôle validateur commercial"
            }
          ],
          actions: [
            {
              type: "notification",
              description: "Notifier le client"
            },
            {
              type: "system",
              description: "Réserver le stock"
            }
          ]
        },
        {
          from: "validated",
          to: "paid",
          name: "Confirmer paiement",
          conditions: [
            {
              type: "payment",
              description: "Paiement reçu et validé"
            }
          ],
          actions: [
            {
              type: "notification",
              description: "Notifier le service logistique"
            },
            {
              type: "system",
              description: "Générer le bon de préparation"
            }
          ]
        },
        {
          from: "paid",
          to: "in_preparation",
          name: "Démarrer préparation",
          roles: ["logistics_operator"],
          actions: [
            {
              type: "system",
              description: "Mettre à jour le statut de préparation"
            }
          ]
        }
      ],
      businessRules: [
        {
          id: "min-order-amount",
          name: "Montant minimum de commande",
          description: "Le montant total de la commande doit être supérieur à 0€",
          implementation: "orderTotal > 0"
        },
        {
          id: "stock-availability",
          name: "Disponibilité stock",
          description: "Le stock doit être disponible pour tous les articles",
          implementation: "allProducts.stock >= orderQuantity"
        }
      ],
      sla: {
        validation: {
          duration: "24h",
          description: "Délai maximum de validation d'une commande"
        },
        preparation: {
          duration: "48h",
          description: "Délai maximum de préparation après paiement"
        }
      },
      kpis: [
        {
          name: "Délai moyen de validation",
          measurement: "Temps moyen entre soumission et validation",
          target: "< 12h"
        },
        {
          name: "Taux d'annulation",
          measurement: "Pourcentage de commandes annulées",
          target: "< 5%"
        }
      ]
    },
    {
      id: "customer-support",
      name: "Support Client",
      description: "Processus de gestion des tickets de support",
      version: "1.5",
      owner: "Service Support",
      category: "Support",
      states: [
        {
          id: "new",
          name: "Nouveau",
          description: "Ticket nouvellement créé",
          isInitial: true,
          metadata: {
            autoAssign: true
          }
        },
        {
          id: "assigned",
          name: "Assigné",
          description: "Ticket assigné à un agent"
        },
        {
          id: "in_progress",
          name: "En cours",
          description: "Ticket en cours de traitement"
        },
        {
          id: "pending_customer",
          name: "En attente client",
          description: "En attente d'une réponse du client"
        },
        {
          id: "resolved",
          name: "Résolu",
          description: "Ticket résolu",
          isFinal: true
        },
        {
          id: "closed",
          name: "Fermé",
          description: "Ticket fermé",
          isFinal: true
        }
      ],
      transitions: [
        {
          from: "new",
          to: "assigned",
          name: "Assigner",
          roles: ["support_supervisor"],
          conditions: [
            {
              type: "agent_availability",
              description: "Agent disponible avec les compétences requises"
            }
          ],
          actions: [
            {
              type: "notification",
              description: "Notifier l'agent assigné"
            }
          ]
        }
      ],
      sla: {
        firstResponse: {
          duration: "4h",
          description: "Délai maximum pour la première réponse"
        },
        resolution: {
          duration: "48h",
          description: "Délai maximum de résolution"
        }
      }
    }
  ]
};

// Composants UI de base
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

const Button = ({ 
  children, 
  className = '', 
  variant = 'default', 
  size = 'default',
  onClick,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors';
  const variants = {
    default: 'bg-blue-500 text-white hover:bg-blue-600',
    outline: 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-700'
  };
  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-8 px-3 text-sm',
    lg: 'h-12 px-6'
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

// Composant de visualisation du diagramme d'état
const ProcessDiagram = ({ process }) => {
  // Création du diagramme Mermaid pour le processus
  const generateMermaidDiagram = () => {
    let diagram = 'stateDiagram-v2\n';
    
    // Ajout des états
    process.states.forEach(state => {
      if (state.isInitial) {
        diagram += `[*] --> ${state.id}\n`;
      }
      if (state.isFinal) {
        diagram += `${state.id} --> [*]\n`;
      }
    });

    // Ajout des transitions
    process.transitions.forEach(transition => {
      diagram += `${transition.from} --> ${transition.to}: ${transition.name}\n`;
    });

    return diagram;
  };

  return (
    <div className="w-full overflow-x-auto">
      <div className="bg-white p-4 rounded-lg shadow">
        <pre className="application/vnd.ant.mermaid">
          {generateMermaidDiagram()}
        </pre>
      </div>
    </div>
  );
};

// Composant de détail d'état
const StateDetail = ({ state }) => {
  return (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${
              state.isInitial ? 'bg-green-500' : 
              state.isFinal ? 'bg-red-500' : 'bg-blue-500'
            }`} />
            <span>{state.name}</span>
          </CardTitle>
          {state.metadata && Object.keys(state.metadata).length > 0 && (
            <div className="flex items-center space-x-2">
              {state.metadata.slaHours && (
                <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  SLA: {state.metadata.slaHours}h
                </span>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-gray-600">{state.description}</p>
        {state.metadata && (
          <div className="mt-4 space-y-2">
            {Object.entries(state.metadata).map(([key, value]) => (
              <div key={key} className="text-sm">
                <span className="font-medium text-gray-700">
                  {key.replace(/_/g, ' ').charAt(0).toUpperCase() + 
                   key.replace(/_/g, ' ').slice(1)}:
                </span>
                <span className="ml-2 text-gray-600">
                  {typeof value === 'boolean' ? (value ? 'Oui' : 'Non') : value}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Composant de détail de transition
const TransitionDetail = ({ transition }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card className="mb-4">
      <CardHeader className="cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <ArrowRight className="h-5 w-5 text-blue-500" />
            <div>
              <h4 className="font-medium text-gray-900">{transition.name}</h4>
              <p className="text-sm text-gray-600">
                De {transition.from} vers {transition.to}
              </p>
            </div>
          </div>
          {isExpanded ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent>
          <div className="space-y-4">
            {transition.conditions && transition.conditions.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-gray-700 mb-2">
                  Conditions de transition
                </h5>
                <div className="space-y-2">
                  {transition.conditions.map((condition, index) => (
                    <div
                      key={index}
                      className="flex items-start space-x-2 text-sm"
                    >
                      <AlertTriangle className="h-4 w-4 text-yellow-500 mt-0.5" />
                      <div>
                        <span className="font-medium">
                          {condition.type.replace(/_/g, ' ')}:
                        </span>
                        <span className="ml-1 text-gray-600">
                          {condition.description}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
			  
			  )}

          {transition.actions && transition.actions.length > 0 && (
            <div className="mt-4">
              <h5 className="text-sm font-medium text-gray-700 mb-2">
                Actions post-transition
              </h5>
              <div className="space-y-2">
                {transition.actions.map((action, index) => (
                  <div
                    key={index}
                    className="flex items-start space-x-2 text-sm"
                  >
                    <Play className="h-4 w-4 text-green-500 mt-0.5" />
                    <div>
                      <span className="font-medium">
                        {action.type.replace(/_/g, ' ')}:
                      </span>
                      <span className="ml-1 text-gray-600">
                        {action.description}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {transition.roles && (
            <div className="mt-4">
              <h5 className="text-sm font-medium text-gray-700 mb-2">
                Rôles autorisés
              </h5>
              <div className="flex flex-wrap gap-2">
                {transition.roles.map((role, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm"
                  >
                    {role.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
      )}
    </Card>
  );
};

// Composant des règles métier
const BusinessRulesSection = ({ rules }) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Règles Métier</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rules.map((rule) => (
          <Card key={rule.id}>
            <CardContent>
              <div className="flex items-start space-x-3">
                <Code className="h-5 w-5 text-blue-500 mt-1" />
                <div>
                  <h4 className="font-medium text-gray-900">{rule.name}</h4>
                  <p className="text-sm text-gray-600 mt-1">{rule.description}</p>
                  {rule.implementation && (
                    <div className="mt-2 bg-gray-50 p-2 rounded text-sm font-mono">
                      {rule.implementation}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Composant SLA et KPIs
const MetricsSection = ({ sla, kpis }) => {
  return (
    <div className="space-y-6">
      {sla && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Accords de Niveau de Service (SLA)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(sla).map(([key, value]) => (
              <Card key={key}>
                <CardContent>
                  <div className="flex items-start space-x-3">
                    <Clock className="h-5 w-5 text-blue-500 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {key.replace(/_/g, ' ').charAt(0).toUpperCase() + 
                         key.replace(/_/g, ' ').slice(1)}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {value.description}
                      </p>
                      <div className="mt-2 text-sm">
                        <span className="font-medium">Durée:</span>
                        <span className="ml-2 bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {value.duration}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {kpis && kpis.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Indicateurs de Performance (KPIs)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {kpis.map((kpi, index) => (
              <Card key={index}>
                <CardContent>
                  <div className="flex items-start space-x-3">
                    <Activity className="h-5 w-5 text-blue-500 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-900">{kpi.name}</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {kpi.measurement}
                      </p>
                      <div className="mt-2 text-sm">
                        <span className="font-medium">Objectif:</span>
                        <span className="ml-2 bg-green-100 text-green-800 px-2 py-1 rounded">
                          {kpi.target}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Composant principal de processus
const ProcessDetail = ({ process }) => {
  const [activeTab, setActiveTab] = useState('diagram');

  return (
    <Card className="mb-8">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <Workflow className="h-6 w-6 text-blue-500" />
              <span>{process.name}</span>
            </CardTitle>
            <p className="mt-1 text-gray-600">{process.description}</p>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
              v{process.version}
            </span>
            <span className="text-sm text-gray-600">
              {process.category}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-6">
          <div className="flex space-x-4 border-b">
            {[
              { id: 'diagram', label: 'Diagramme', icon: GitBranch },
              { id: 'states', label: 'États', icon: Square },
              { id: 'transitions', label: 'Transitions', icon: ArrowRight },
              { id: 'rules', label: 'Règles Métier', icon: Code },
              { id: 'metrics', label: 'SLA & KPIs', icon: Activity }
            ].map(tab => (
              <button
                key={tab.id}
                className={`flex items-center space-x-2 px-4 py-2 border-b-2 font-medium ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          {activeTab === 'diagram' && (
            <ProcessDiagram process={process} />
          )}

          {activeTab === 'states' && (
            <div className="space-y-4">
              {process.states.map(state => (
                <StateDetail key={state.id} state={state} />
              ))}
            </div>
          )}

          {activeTab === 'transitions' && (
            <div className="space-y-4">
              {process.transitions.map((transition, index) => (
                <TransitionDetail key={index} transition={transition} />
              ))}
            </div>
          )}

          {activeTab === 'rules' && (
            <BusinessRulesSection rules={process.businessRules || []} />
          )}

          {activeTab === 'metrics' && (
            <MetricsSection sla={process.sla} kpis={process.kpis} />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Composant principal de documentation des processus
const DocumentProcess = () => {
  const [processData, setProcessData] = useState(PROCESS_DOCUMENTATION);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* En-tête */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {processData.metadata.title}
            </h1>
            <p className="mt-1 text-gray-600">
              Version {processData.metadata.version} - 
              Dernière mise à jour : {processData.metadata.lastUpdate}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center space-x-2"
          >
            {isEditing ? (
              <>
                <Save className="h-4 w-4" />
                <span>Sauvegarder</span>
              </>
            ) : (
              <>
                <Edit className="h-4 w-4" />
                <span>Modifier</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Barre de recherche */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un processus..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
      </div>

      {/* Liste des processus */}
      <div className="space-y-8">
        {processData.processes
          .filter(process =>
            process.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            process.description.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .map((process) => (
            <ProcessDetail key={process.id} process={process} />
          ))}
      </div>

      {/* Bouton d'ajout de processus */}
      {isEditing && (
        <div className="fixed bottom-4 right-4">
          <Button
            onClick={() => {
              console.log('Add new process');
            }}
            className="flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Ajouter un processus</span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default DocumentProcess;